<template>
  <new-edit-page
    :is-loading="loading"
    :page-title="pageTitle"
    :submit-button-label="submitButtonLabel"
    :submit-button-disabled="submitButtonDisabled"
    @submit="saveObject"
    style="width: 100%;"
  >
    <template v-slot:leftSideContent>
      <form class="management-form">
        <div v-if="type === 'comment'" class="input-field-container large">
          <label class="required">{{ $t("Description") }}</label>
          <textarea
            v-if="comment"
            v-model="comment.text"
            id="description"
            required
            class="input-field"
          />
        </div>
        <div v-if="type === 'ticket'" class="input-field-container large">
          <label for="summary" class="required">{{ $t("Title") }}</label>
          <input
            v-model="ticket.summary"
            type="text"
            id="summary"
            required
            class="input-field"
          />
        </div>
      </form>
    </template>

    <template v-slot:rightSideHeader>
      <management-tabs-and-search
        :tabs="tabs"
        :open-tab="type === 'ticket' ? openTab : 'attachments'"
        @update-open-tab="
          if (type === 'ticket') {
            openTab = $event;
          }
        "
        style="float: left"
      />
      <v-layout v-if="showAttachmentsButton" row justify-end>
        <v-btn
          @click="openAttachmentForm"
          icon
          text
          color="accent"
          class="ma-0"
        >
          <v-icon>add_circle</v-icon>
        </v-btn>
      </v-layout>
    </template>

    <template v-slot:rightSideContent>
      <v-tabs-items :value="type === 'ticket' ? openTab : 'attachments'">
        <v-tab-item
          v-if="type === 'ticket'"
          value="description"
          class="elevation-2"
        >
          <textarea
            v-model="ticket.description"
            required
            id="description"
            class="input-field"
            style="border: none; height: 382px"
          />
        </v-tab-item>
        <v-tab-item value="attachments" class="elevation-2">
          <attachments-table
            :attachments="attachmentToShow"
            @open-attachment="openAttachment"
            @remove-attachment="removeAttachment($event)"
          />
        </v-tab-item>
      </v-tabs-items>
      <attachment-form
        :open="attachmentFormIsOpen"
        @add-attachment="saveAttachmentLocally($event)"
        @close="closeAttachmentForm"
      />
    </template>
  </new-edit-page>
</template>

<script>
import { mapActions } from "vuex";
import NewEditPage from "@/components/Management/NewEditPage/NewEditPage";
import ManagementTabsAndSearch from "@/components/_Organisms/ManagementTabsAndSearch";
import _ from "lodash";
import AttachmentsTable from "@/components/Support/TechnicalSupport/AttachmentsTable";
import AttachmentForm from "@/components/Support/TechnicalSupport/AttachmentForm";

export default {
  name: "SupportObjectCreateEdit",

  components: {
    NewEditPage,
    ManagementTabsAndSearch,
    AttachmentsTable,
    AttachmentForm
  },

  data() {
    return {
      loading: false,
      openTab: "description",
      attachmentFormIsOpen: false,

      ticket: {
        description: "",
        summary: "",
        attachments: []
      },

      comment: {
        text: "",
        attachments: []
      },

      newAttachments: []
    };
  },

  computed: {
    type() {
      let routeName = this.$route.name;
      if (routeName === "comments-edit" || routeName === "comments-create") {
        return "comment";
      } else if (
        routeName === "tickets-edit" ||
        routeName === "tickets-create"
      ) {
        return "ticket";
      }
    },

    ticketID() {
      return this.$route.params.id;
    },

    commentID() {
      return this.$route.params.commentId;
    },

    actionType() {
      let routeName = this.$route.name;
      if (routeName === "comments-edit" || routeName === "tickets-edit") {
        return "edit";
      } else if (
        routeName === "comments-create" ||
        routeName === "tickets-create"
      ) {
        return "create";
      }
    },

    pageTitle() {
      if (this.type === "comment") {
        return this.actionType === "create"
          ? this.$t("NewComment")
          : this.$t("EditComment");
      } else {
        return this.actionType === "create"
          ? this.$t("NewTicket")
          : this.$t("EditTicket");
      }
    },

    submitButtonLabel() {
      return this.actionType === "create" ? this.$t("Create") : this.$t("Save");
    },

    submitButtonDisabled() {
      if (this.type === "comment" && this.comment) {
        return this.comment.text === "";
      }
      if (this.type === "ticket" && this.ticket) {
        return this.ticket.summary === "" || this.ticket.description === "";
      }
    },

    tabs() {
      let tabs = [];
      if (this.type === "ticket") {
        tabs.push({
          value: "description",
          text: this.$i18n.t("Description"),
          hasSearch: true
        });
      }
      tabs.push({
        value: "attachments",
        text: this.$i18n.tc("Attachment", 2)
      });
      return tabs;
    },

    showAttachmentsButton() {
      return this.type === "comment" ||
        (this.type === "ticket" && this.openTab === "attachments")
        ? true
        : false;
    },

    attachmentToShow() {
      if (this.actionType === "edit") {
        return this.type === "ticket"
          ? this.ticket.attachments
          : this.comment.attachments;
      } else {
        return this.newAttachments;
      }
    }
  },

  mounted() {
    if (this.actionType === "edit") {
      this.getObject();
    }
  },

  methods: {
    ...mapActions([
      "createComment",
      "fetchCommentByID",
      "editComment",
      "createCommentAttachment",
      "createTicket",
      "fetchTicketByID",
      "editTicket",
      "createTicketAttachment"
    ]),

    async getObject() {
      if (this.ticketID) {
        this.loading = true;
        try {
          let object;
          if (this.commentID) {
            object = await this.fetchCommentByID({
              commentID: this.commentID,
              ticketID: this.ticketID
            });
          } else {
            object = await this.fetchTicketByID(this.ticketID);
          }

          if (object.error === "Not Found") {
            this.$router.push("/error");
          }

          if (this.type === "ticket") {
            this.ticket = object;
          } else {
            this.comment = object;
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
        }
      }
    },

    async saveObject() {
      this.loading = true;
      try {
        if (this.type === "comment") {
          let dataToSend = _.cloneDeep(this.comment);
          if (this.actionType === "create") {
            let formData = this.addAttachmentsToFormData();
            formData.append("text", dataToSend.text);

            await this.createComment({
              formData,
              ticketID: this.ticketID
            });
          } else {
            await this.editComment({
              newData: dataToSend.text,
              ticketID: this.ticketID,
              commentID: this.commentID
            });
          }
        } else {
          let dataToSend = _.cloneDeep(this.ticket);
          if (this.actionType === "create") {
            let formData = this.addAttachmentsToFormData();
            formData.append("description", dataToSend.description);
            formData.append("summary", dataToSend.summary);

            await this.createTicket(formData);
          } else {
            await this.editTicket({
              newData: dataToSend,
              ticketID: this.ticketID
            });
          }
        }
        this.$router.back();
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    openAttachment(attachment) {
      window.open(attachment.url);
    },

    saveAttachmentLocally(newAttachment) {
      this.loading = true;

      if (newAttachment instanceof File) {
        this.newAttachments.push(newAttachment);
      } else {
        this.newAttachments = this.newAttachments.concat(
          Array.from(newAttachment)
        );
      }

      if (this.actionType === "edit") {
        this.saveAttachment();
      }

      this.loading = false;
    },

    async saveAttachment() {
      this.loading = true;
      let formData = this.addAttachmentsToFormData();

      try {
        if (this.type === "ticket") {
          this.ticket = await this.createTicketAttachment({
            formData,
            ticketID: this.ticketID
          });
        } else {
          this.comment = await this.createCommentAttachment({
            formData,
            ticketID: this.ticketID,
            commentID: this.commentID
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
        this.newAttachments = [];
        this.getObject();
      }
    },

    addAttachmentsToFormData() {
      let formData = new FormData();
      for (let i = 0; i < this.newAttachments.length; i++) {
        formData.append("upload[]", this.newAttachments[i]);
      }
      return formData;
    },

    openAttachmentForm() {
      this.attachmentFormIsOpen = true;
    },

    closeAttachmentForm() {
      this.attachmentFormIsOpen = false;
    },

    removeAttachment(i) {
      this.newAttachments.splice(i, 1);
    }
  }
};
</script>

<style scoped>
#description {
  padding: 20px;
  height: 422px;
}
</style>



// WEBPACK FOOTER //
// src/components/Support/TechnicalSupport/SupportObjectCreateEdit.vue